<script setup>
import {computed, onMounted, reactive, ref, watch} from "vue";
import {useRouter} from "vue-router";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import {useToast} from "vue-toastification";
import urlSafeTitle from "@/Helpers/UrlTitleHelper";
import ConfirmationModal from "@/Components/ConfirmationModal.vue";
import {useUserStore} from "@/Stores/userStore";
import favoritesApi from "@/Api/favorites-api";
import FavoriteIcon from "@/Components/Favorites/FavoriteIcon.vue";

const emit = defineEmits(['remove-item']);

const router = useRouter();
const toast = useToast();
const userStore = useUserStore();

const imageQueue = ref([]);
const loadedImages = ref([]);

const props = defineProps({
    content: {
        type: Object,
        required: true,
    },
    favorites: {
        type: Object,
        required: false,
    },
});

const data = reactive({
    shouldDisplayDestroyOption: false,
    selectedItem: null,
})

function routeToContent(item) {
    router.push({name: 'creature', query: {creature_id: item.id, name: urlSafeTitle(item.name)}})
}

function isFavoriteStatusKnown(item) {
    if (props.favorites && props.favorites.creatures) {
        return props.favorites.creatures.hasOwnProperty(item.id) && props.favorites.creatures[item.id] !== undefined;
    }
    return false;
}

function isFavorited(item) {
    if (props.favorites && props.favorites.creatures) {
        if (props.favorites.creatures.hasOwnProperty(item.id)) {
            return props.favorites.creatures[item.id];
        }
    }
    return false;
}

const contentUpdated = ref(false);
const animatedItems = new Set();

watch(
    () => props.content,
    (newContent, oldContent) => {
        contentUpdated.value = true;
        console.log("content updated");
        setTimeout(() => {
            contentUpdated.value = false;
            for (const item of newContent) {
                animatedItems.add(item.id);
            }
            enqueueImages();
            loadNext();
            loadNext();
            loadNext();
        }, 500);
    },
    { deep: true }
);

function descriptionTruncated(description) {
    if (description.length > 400) {
        return description.substring(0, 400) + "...";
    }
    return description;
}

const loadNext = () => {
    if (imageQueue.value.length > 0) {
        const nextImageId = imageQueue.value.shift();
        loadedImages.value.push(nextImageId);
    }
};

const enqueueImages = () => {
    imageQueue.value = [];
    for (const content of props.content) {
        if (content.illustration)
        {
            imageQueue.value.push(content.illustration.id);
        }
    }
};

function shouldLoadImage(id) {
    if (id == null) {
        return false;
    }
    return loadedImages.value.includes(id);
}

const creatureRoute = computed(() => ({
    name: 'creature',
    query: {
        creature_id: props.item.id,
        name: urlSafeTitle(props.item.name),
    },
}));

onMounted(() => {
    // Load the first two images immediately (This will cause two images to load concurrently the entire time.)
    enqueueImages();
    loadNext();
    loadNext();
    loadNext();
})

</script>

<template>
    <div class="max-w-5xl w-full">
        <div>
            <transition-group name="list" tag="ul" role="list">
                <li
                    v-for="item in props.content"
                    :key="item.id"
                    class="mt-8"
                >
                    <div class="block bg-gray-800 rounded-lg border border-gray-500 md:py-6 py-0 px-6 relative">
                        <div class="grid grid-cols-1 md:grid-cols-4 grid-rows-1">
                            <img v-if="shouldLoadImage(item.illustration?.id)"
                                class="rounded-md mt-6 md:mt-0"
                                :src="`${item.illustration.url}?resolution=256x256`"
                                :alt="item.name"
                                @load="loadNext()"
                            />
                            <div v-else  class="rounded-md mt-6 md:mt-0 bg-gray-900" style="width: 246px; height: 256px;"></div>
                            <div class="flex-col justify-between md:px-4 px-0 md:py-0 py-4 text-white space-y-4 col-span-3">
                                <div class="flex justify-between items-center">
                                    <p v-if="item.name" class="text-xl font-semibold">{{item.name}}</p>
                                    <div class="flex items-center">
                                        <span v-if="item.edition" class="h-5 inline-flex items-center rounded-md bg-blue-800 py-1.5 px-6 text-sm font-medium text-blue-100">{{ item.edition }}</span>
                                        <FavoriteIcon :key="item.id" :has-loaded="isFavoriteStatusKnown(item)" :is-favorite="isFavorited(item)" :item="item"/>
                                    </div>
                                </div>
                                <p v-if="item.description">{{descriptionTruncated(item.description)}}</p>
                                <div>
                                    <p v-if="item.challenge_rating"><span class="font-semibold">Challenge Rating:</span> <span class="font-normal">{{item.challenge_rating}}</span></p>
                                    <p v-if="item.hit_points"><span class="font-semibold">Hit Points:</span> <span class="font-normal">{{item.hit_points}}</span></p>
                                </div>
                            </div>
                        </div>
                        <router-link :to="{name: 'creature', query: {creature_id: item.id, name: urlSafeTitle(item.name)}}">
                            <PrimaryButton class="!bg-gray-700 absolute md:bottom-5 bottom-4 right-4">
                                View
                            </PrimaryButton>
                        </router-link>
                    </div>
                </li>
            </transition-group>
        </div>
    </div>
</template>


<style scoped>
.list-enter-active,
.list-leave-active {
    @apply duration-[500ms];
}

.list-enter-from,
.list-leave-to {
    @apply transform -translate-y-4 opacity-0;
}

.list-enter-to,
.list-leave-from {
    @apply transform translate-y-0 opacity-100;
}

.list-move {
    @apply transition-all duration-[250ms] ease-out;
}
</style>
